import {
  animate,
  motion,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useTransform,
} from 'motion/react';
import { useEffect, useRef, useState } from 'react';

/* ─── Animation tuning constants ─────────────────────────────────── */

const ANIM = {
  particle: { duration: 3.2, trailCount: 3, trailSpacing: 0.06 },
  pulse: { scale: 1.04, duration: 0.35 },
  glow: { stdDev: [4, 10, 4] as readonly number[], duration: 0.5 },
  path: { dashArray: '6 4', glowWidth: 60 },
  label: { breatheDuration: 3.5 },
  stagger: { path2Delay: 1.6 },
} as const;

/* ─── Path geometry ──────────────────────────────────────────────── */

const PATHS = {
  // Vault → Agent (y=50: below the ALIAS label to avoid overlap)
  p1: { x1: 100, y1: 50, x2: 195, y2: 50 },
  // Agent → Proxy (y=50: below the RESOLVES label to avoid overlap)
  p2: { x1: 290, y1: 50, x2: 385, y2: 50 },
} as const;

/* ─── Reusable particle trail sub-component ──────────────────────── */

interface ParticleTrailProps {
  progress: ReturnType<typeof useMotionValue<number>>;
  path: (typeof PATHS)['p1'];
  color: string;
  glowFilterId: string;
  gradientId: string;
}

function ParticleTrail({ progress, path, color, glowFilterId, gradientId }: ParticleTrailProps) {
  const dx = path.x2 - path.x1;
  const dy = path.y2 - path.y1;

  // Lead particle position
  const cx = useTransform(progress, [0, 1], [path.x1, path.x2]);
  const cy = useTransform(progress, [0, 1], [path.y1, path.y2]);

  // Tail endpoint (trails behind the lead by a fraction)
  const tailOffset = 0.12;
  const tailX = useTransform(progress, (p) => {
    const tailP = Math.max(0, p - tailOffset);
    return path.x1 + dx * tailP;
  });
  const tailY = useTransform(progress, (p) => {
    const tailP = Math.max(0, p - tailOffset);
    return path.y1 + dy * tailP;
  });

  // Particle size pulsation
  const radius = useTransform(progress, (p) => {
    // Pulsate faster: 3 cycles per trip
    const pulse = Math.sin(p * Math.PI * 6);
    return 3 + pulse * 1.2;
  });

  // Overall opacity — fade in at start, fade out at end
  const opacity = useTransform(progress, [0, 0.05, 0.15, 0.85, 0.95, 1], [0, 0.6, 1, 1, 0.6, 0]);

  // Tail opacity (dimmer than lead)
  const tailOpacity = useTransform(opacity, (o) => o * 0.3);

  // Gradient stop position follows particle
  const gradStop = useTransform(progress, (p) => `${(p * 100).toFixed(1)}%`);
  const gradStopBefore = useTransform(progress, (p) => `${Math.max(0, p * 100 - 15).toFixed(1)}%`);
  const gradStopAfter = useTransform(progress, (p) => `${Math.min(100, p * 100 + 15).toFixed(1)}%`);

  // Trail particles (smaller, dimmer, delayed)
  const trailParticles = Array.from({ length: ANIM.particle.trailCount - 1 }, (_, i) => {
    const delay = (i + 1) * ANIM.particle.trailSpacing;
    return { delay, scale: 1 - (i + 1) * 0.3, opacityScale: 1 - (i + 1) * 0.35, key: i };
  });

  return (
    <g>
      {/* Fiber-optic glow along the path — gradient follows particle position */}
      <motion.line
        x1={path.x1}
        y1={path.y1}
        x2={path.x2}
        y2={path.y2}
        stroke={`url(#${gradientId})`}
        strokeWidth="6"
        strokeLinecap="round"
        style={{ opacity: tailOpacity }}
      />

      {/* Dynamic gradient that tracks particle */}
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <motion.stop offset={gradStopBefore} stopColor={color} stopOpacity="0" />
          <motion.stop offset={gradStop} stopColor={color} stopOpacity="0.6" />
          <motion.stop offset={gradStopAfter} stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Comet tail — line from trail to lead */}
      <motion.line
        x1={tailX}
        y1={tailY}
        x2={cx}
        y2={cy}
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        style={{ opacity: tailOpacity }}
        filter={`url(#${glowFilterId})`}
      />

      {/* Trail particles */}
      {trailParticles.map(({ delay, scale, opacityScale, key }) => (
        <TrailDot
          key={key}
          progress={progress}
          path={path}
          delay={delay}
          scale={scale}
          opacityScale={opacityScale}
          color={color}
          parentOpacity={opacity}
        />
      ))}

      {/* Lead particle — bright, pulsating, glowing */}
      <motion.circle
        cx={cx}
        cy={cy}
        r={radius}
        fill={color}
        filter={`url(#${glowFilterId})`}
        style={{ opacity }}
      />
    </g>
  );
}

/* ─── Individual trail dot ───────────────────────────────────────── */

interface TrailDotProps {
  progress: ReturnType<typeof useMotionValue<number>>;
  path: (typeof PATHS)['p1'];
  delay: number;
  scale: number;
  opacityScale: number;
  color: string;
  parentOpacity: ReturnType<typeof useTransform<number, number>>;
}

function TrailDot({ progress, path, delay, scale, opacityScale, color, parentOpacity }: TrailDotProps) {
  const dx = path.x2 - path.x1;
  const dy = path.y2 - path.y1;

  const trailCx = useTransform(progress, (p) => {
    const tp = Math.max(0, p - delay);
    return path.x1 + dx * tp;
  });
  const trailCy = useTransform(progress, (p) => {
    const tp = Math.max(0, p - delay);
    return path.y1 + dy * tp;
  });
  const trailR = useTransform(progress, (p) => {
    const pulse = Math.sin(p * Math.PI * 6);
    return (2 + pulse * 0.6) * scale;
  });
  const trailOpacity = useTransform(parentOpacity, (o) => o * opacityScale);

  return (
    <motion.circle
      cx={trailCx}
      cy={trailCy}
      r={trailR}
      fill={color}
      style={{ opacity: trailOpacity }}
    />
  );
}

/* ─── Main component ─────────────────────────────────────────────── */

export function SecurityFlowDiagram({ className = '' }: { className?: string }) {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const prefersReduced = useReducedMotion();

  // Progress values for each path (0 → 1)
  const progress1 = useMotionValue(0);
  const progress2 = useMotionValue(0);

  // Node pulse state
  const [pulsingNode, setPulsingNode] = useState<string | null>(null);
  const pulseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Start animations
  useEffect(() => {
    if (prefersReduced) return;

    let ctrl2: ReturnType<typeof animate> | null = null;

    const ctrl1 = animate(progress1, [0, 1], {
      duration: ANIM.particle.duration,
      repeat: Infinity,
      ease: [0.25, 0.1, 0.25, 1],
    });

    // Staggered second path
    const timer = setTimeout(() => {
      ctrl2 = animate(progress2, [0, 1], {
        duration: ANIM.particle.duration,
        repeat: Infinity,
        ease: [0.25, 0.1, 0.25, 1],
      });
    }, ANIM.stagger.path2Delay * 1000);

    return () => {
      ctrl1.stop();
      ctrl2?.stop();
      clearTimeout(timer);
    };
  }, [prefersReduced, progress1, progress2]);

  // Node pulse-on-arrival: path 1 → agent node
  useMotionValueEvent(progress1, 'change', (v) => {
    if (v > 0.92 && v < 0.98 && pulsingNode !== 'agent') {
      setPulsingNode('agent');
      if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current);
      pulseTimeoutRef.current = setTimeout(() => setPulsingNode(null), ANIM.pulse.duration * 1000);
    }
  });

  // Node pulse-on-arrival: path 2 → proxy node
  useMotionValueEvent(progress2, 'change', (v) => {
    if (v > 0.92 && v < 0.98 && pulsingNode !== 'proxy') {
      setPulsingNode('proxy');
      if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current);
      pulseTimeoutRef.current = setTimeout(() => setPulsingNode(null), ANIM.pulse.duration * 1000);
    }
  });

  const nodeDescriptions: Record<string, string> = {
    vault: 'Your API keys are encrypted at rest using AES-256. Only the proxy can decrypt them \u2014 never the AI.',
    agent: 'The AI agent only sees safe aliases like STRIPE_KEY. It has no access to real credentials.',
    proxy: 'The security proxy swaps aliases for real keys at the network boundary, just before the request leaves.',
  };

  // Shared entrance transition — staggered spring
  const nodeEntrance = (delay: number) =>
    prefersReduced
      ? {}
      : {
          initial: { opacity: 0, scale: 0.85 },
          animate: { opacity: 1, scale: 1 },
          transition: { type: 'spring' as const, stiffness: 300, damping: 24, delay },
        };

  // Pulsing glow animation for node borders
  const pulseGlow = prefersReduced
    ? {}
    : {
        animate: { strokeOpacity: [0.9, 1, 0.9] },
        transition: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' as const },
      };

  // Node pulse-on-arrival: enhance the rect's strokeWidth briefly
  const nodePulseRect = (nodeId: string) =>
    pulsingNode === nodeId && !prefersReduced
      ? {
          animate: { strokeWidth: [1.5, 2.5, 1.5] },
          transition: { duration: ANIM.pulse.duration },
        }
      : {};

  return (
    <div className={`relative ${className}`}>
      {/* Flow diagram — responsive SVG, capped width to prevent stretch on wide containers */}
      <svg
        viewBox="0 0 520 85"
        className="w-full h-auto max-w-[520px] mx-auto"
        role="img"
        aria-label="Zero-trust credential flow: credentials are stored in a vault, the AI agent only sees tokenized aliases, the security proxy resolves real credentials at the network boundary"
      >
        <defs>
          {/* Gradient for connection paths */}
          <linearGradient id="sf-grad-warm" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: 'var(--sd-color-warning)', stopOpacity: 0.5 }} />
            <stop offset="100%" style={{ stopColor: 'var(--sd-color-warning)', stopOpacity: 0.15 }} />
          </linearGradient>
          <linearGradient id="sf-grad-cool" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: 'var(--sd-color-success)', stopOpacity: 0.15 }} />
            <stop offset="100%" style={{ stopColor: 'var(--sd-color-success)', stopOpacity: 0.5 }} />
          </linearGradient>

          {/* Glow filters — bright, layered bloom for particles */}
          <filter id="sf-glow-warm" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="4" result="blur1" />
            <feGaussianBlur stdDeviation="8" result="blur2" />
            <feMerge>
              <feMergeNode in="blur2" />
              <feMergeNode in="blur1" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="sf-glow-cool" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="4" result="blur1" />
            <feGaussianBlur stdDeviation="8" result="blur2" />
            <feMerge>
              <feMergeNode in="blur2" />
              <feMergeNode in="blur1" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Node glow on hover / pulse */}
          <filter id="sf-node-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Pulse-on-arrival glow burst — wider spread */}
          <filter id="sf-pulse-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Connection line 1: Vault → Agent (line-drawing entrance + dashes) */}
        <motion.line
          x1={PATHS.p1.x1}
          y1={PATHS.p1.y1}
          x2={PATHS.p1.x2}
          y2={PATHS.p1.y2}
          stroke="url(#sf-grad-warm)"
          strokeWidth="1.5"
          strokeDasharray={ANIM.path.dashArray}
          {...(prefersReduced
            ? {}
            : {
                initial: { pathLength: 0, opacity: 0 },
                animate: { pathLength: 1, opacity: 1 },
                transition: { duration: 0.6, delay: 0.3, ease: 'easeOut' },
              })}
          className="sd-flow-line"
        />

        {/* Connection line 2: Agent → Proxy (line-drawing entrance + dashes) */}
        <motion.line
          x1={PATHS.p2.x1}
          y1={PATHS.p2.y1}
          x2={PATHS.p2.x2}
          y2={PATHS.p2.y2}
          stroke="url(#sf-grad-cool)"
          strokeWidth="1.5"
          strokeDasharray={ANIM.path.dashArray}
          {...(prefersReduced
            ? {}
            : {
                initial: { pathLength: 0, opacity: 0 },
                animate: { pathLength: 1, opacity: 1 },
                transition: { duration: 0.6, delay: 0.6, ease: 'easeOut' },
              })}
          className="sd-flow-line"
        />

        {/* Animated particle trails */}
        {!prefersReduced && (
          <>
            <ParticleTrail
              progress={progress1}
              path={PATHS.p1}
              color="var(--sd-color-warning)"
              glowFilterId="sf-glow-warm"
              gradientId="sf-fiber-warm"
            />
            <ParticleTrail
              progress={progress2}
              path={PATHS.p2}
              color="var(--sd-color-success)"
              glowFilterId="sf-glow-cool"
              gradientId="sf-fiber-cool"
            />
          </>
        )}

        {/* Node 1: Vault — spring entrance, pulsing border */}
        <motion.g
          onMouseEnter={() => setHoveredNode('vault')}
          onMouseLeave={() => setHoveredNode(null)}
          className="cursor-pointer"
          role="graphics-symbol"
          aria-label="Encrypted vault"
          {...nodeEntrance(0)}
        >
          <motion.rect
            x="12"
            y="16"
            width="80"
            height="48"
            rx="8"
            fill="var(--sd-color-surface-elevated)"
            stroke="var(--sd-color-warning)"
            strokeWidth="1.5"
            filter={hoveredNode === 'vault' ? 'url(#sf-node-glow)' : undefined}
            {...pulseGlow}
          />
          {/* Lock icon */}
          <g transform="translate(40, 32)">
            <rect
              x="-7"
              y="-2"
              width="14"
              height="11"
              rx="2"
              fill="none"
              stroke="var(--sd-color-warning)"
              strokeWidth="1.2"
            />
            <path
              d="M-4,-2 L-4,-6 A4,4 0 0,1 4,-6 L4,-2"
              fill="none"
              stroke="var(--sd-color-warning)"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
            <circle cx="0" cy="3" r="1.5" fill="var(--sd-color-warning)" />
          </g>
          <text
            x="52"
            y="55"
            textAnchor="middle"
            fill="var(--sd-color-text-secondary)"
            style={{ fontSize: '9px', fontWeight: 500, letterSpacing: '0.05em' }}
          >
            VAULT
          </text>
        </motion.g>

        {/* Label: ALIAS — subtle breathing animation */}
        <motion.g
          {...nodeEntrance(0.2)}
          {...(prefersReduced
            ? {}
            : {
                animate: { opacity: [0.75, 1, 0.75] },
                transition: {
                  duration: ANIM.label.breatheDuration,
                  repeat: Infinity,
                  ease: 'easeInOut' as const,
                },
              })}
        >
          <rect x="118" y="27" width="60" height="16" rx="8" fill="var(--sd-color-warning)" opacity="0.1" />
          <text
            x="148"
            y="38.5"
            textAnchor="middle"
            fill="var(--sd-color-warning)"
            style={{ fontSize: '7.5px', fontWeight: 600, letterSpacing: '0.08em' }}
          >
            ALIAS
          </text>
        </motion.g>

        {/* Node 2: AI Agent — pulses on particle arrival */}
        <motion.g
          onMouseEnter={() => setHoveredNode('agent')}
          onMouseLeave={() => setHoveredNode(null)}
          className="cursor-pointer"
          role="graphics-symbol"
          aria-label="AI Agent sees only aliases"
          {...nodeEntrance(0.35)}
        >
          <motion.rect
            x="200"
            y="16"
            width="80"
            height="48"
            rx="8"
            fill="var(--sd-color-surface-elevated)"
            stroke="var(--sd-color-primary)"
            strokeWidth="1.5"
            filter={
              pulsingNode === 'agent'
                ? 'url(#sf-pulse-glow)'
                : hoveredNode === 'agent'
                  ? 'url(#sf-node-glow)'
                  : undefined
            }
            {...pulseGlow}
            {...nodePulseRect('agent')}
          />
          {/* Brain/AI icon */}
          <g transform="translate(240, 34)">
            <circle cx="0" cy="0" r="8" fill="none" stroke="var(--sd-color-primary)" strokeWidth="1.2" />
            <path
              d="M-3,-3 Q0,-6 3,-3 M-4,0 Q0,4 4,0"
              fill="none"
              stroke="var(--sd-color-primary)"
              strokeWidth="1"
              strokeLinecap="round"
            />
            <circle cx="0" cy="-1" r="1.2" fill="var(--sd-color-primary)" />
          </g>
          <text
            x="240"
            y="55"
            textAnchor="middle"
            fill="var(--sd-color-text-secondary)"
            style={{ fontSize: '9px', fontWeight: 500, letterSpacing: '0.05em' }}
          >
            AI AGENT
          </text>
        </motion.g>

        {/* Label: RESOLVES — subtle breathing animation */}
        <motion.g
          {...nodeEntrance(0.5)}
          {...(prefersReduced
            ? {}
            : {
                animate: { opacity: [0.75, 1, 0.75] },
                transition: {
                  duration: ANIM.label.breatheDuration,
                  repeat: Infinity,
                  ease: 'easeInOut' as const,
                  delay: 1.5,
                },
              })}
        >
          <rect x="305" y="27" width="60" height="16" rx="8" fill="var(--sd-color-success)" opacity="0.1" />
          <text
            x="335"
            y="38.5"
            textAnchor="middle"
            fill="var(--sd-color-success)"
            style={{ fontSize: '7.5px', fontWeight: 600, letterSpacing: '0.08em' }}
          >
            RESOLVES
          </text>
        </motion.g>

        {/* Node 3: Security Proxy — pulses on particle arrival */}
        <motion.g
          onMouseEnter={() => setHoveredNode('proxy')}
          onMouseLeave={() => setHoveredNode(null)}
          className="cursor-pointer"
          role="graphics-symbol"
          aria-label="Security proxy resolves credentials"
          {...nodeEntrance(0.6)}
        >
          <motion.rect
            x="390"
            y="16"
            width="110"
            height="48"
            rx="8"
            fill="var(--sd-color-surface-elevated)"
            stroke="var(--sd-color-success)"
            strokeWidth="1.5"
            filter={
              pulsingNode === 'proxy'
                ? 'url(#sf-pulse-glow)'
                : hoveredNode === 'proxy'
                  ? 'url(#sf-node-glow)'
                  : undefined
            }
            {...pulseGlow}
            {...nodePulseRect('proxy')}
          />
          {/* Shield icon */}
          <g transform="translate(420, 34)">
            <path
              d="M0,-9 L7,-5 L7,2 Q7,8 0,10 Q-7,8 -7,2 L-7,-5 Z"
              fill="none"
              stroke="var(--sd-color-success)"
              strokeWidth="1.2"
            />
            <path
              d="M-3,0 L-1,3 L4,-3"
              fill="none"
              stroke="var(--sd-color-success)"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <text
            x="460"
            y="36"
            textAnchor="middle"
            fill="var(--sd-color-text-secondary)"
            style={{ fontSize: '8px', fontWeight: 500 }}
          >
            SECURITY
          </text>
          <text
            x="460"
            y="47"
            textAnchor="middle"
            fill="var(--sd-color-text-secondary)"
            style={{ fontSize: '8px', fontWeight: 500 }}
          >
            PROXY
          </text>
        </motion.g>
      </svg>

      {/* Hover tooltip — contextual education */}
      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-200 ease-out ${hoveredNode ? 'max-h-[60px] opacity-100' : 'max-h-0 opacity-0'}`}
        aria-live="polite"
      >
        <p className="text-[11px] leading-relaxed text-sd-text-muted mt-sd-1 px-sd-1">
          {hoveredNode ? nodeDescriptions[hoveredNode] : ''}
        </p>
      </div>
    </div>
  );
}
